import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectInput
} from 'blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';

interface EpxCaregiverData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  times: string[];
  days: number[];
  relationship?: string;
}

type EpxCaregiverDataRaw = DateSerialized<EpxCaregiverData, 'start' | 'end'>;

export const EpxCaregiverDefaultData: EpxCaregiverData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  times: ['10:00'],
  days: [3],
  relationship: ''
};

const DATE_FORMAT = 'YYYY-MM-DD';
const RELATIONSHIPS = [
  {
    label: 'Not Known',
    value: ''
  },
  {
    label: 'Wife',
    value: 'wife'
  },
  {
    label: 'Husband',
    value: 'husband'
  },
  {
    label: 'Partner',
    value: 'partner'
  },
  {
    label: 'Mother',
    value: 'mother'
  },
  {
    label: 'Father',
    value: 'father'
  },
  {
    label: 'Father-In-Law',
    value: 'father-in-law'
  },
  {
    label: 'Mother-In-Law',
    value: 'mother-in-law'
  },
  {
    label: 'Grandmother',
    value: 'grandmother'
  },
  {
    label: 'Grandfather',
    value: 'grandfather'
  },
  {
    label: 'Brother',
    value: 'brother'
  },
  {
    label: 'Sister',
    value: 'sister'
  },
  {
    label: 'Son',
    value: 'son'
  },
  {
    label: 'Daughter',
    value: 'daughter'
  },
  {
    label: 'Other',
    value: ''
  }
];

const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxCaregiverFormProps {
  setForm?: (form: FormInstance<EpxCaregiverDataRaw>) => void;
  onSubmit?: (data: EpxCaregiverData) => void;
  isDisabled?: boolean;
}

const EpxCaregiverForm = ({
  setForm,
  onSubmit,
  isDisabled
}: EpxCaregiverFormProps): JSX.Element => {
  const serialize = (data: EpxCaregiverDataRaw): EpxCaregiverData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT)
    };
  };
  const deserialize = (data: EpxCaregiverData): EpxCaregiverDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxCaregiverDefaultData);
  const form = new FormInstance<EpxCaregiverDataRaw, EpxCaregiverData>(
    defaultData,
    {
      serialize,
      deserialize
    }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {() => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectInput
                      label="Relationship to Care Receiver"
                      name="relationship"
                      fill={true}
                      options={RELATIONSHIPS}
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <Weekday name="days" numItems={1} abbreviated inline fill />
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxCaregiverForm;
